/**
 * Schémas de la configuration système et des règles d'alerte.
 *
 * CCT § 6 : « Toute configuration système doit être pilotable depuis l'UI par
 * un non-développeur. » Ces schémas sont ce que l'écran manipule et ce que
 * l'API valide — le même objet des deux côtés.
 */
import { z } from 'zod';
import { ROLES } from '../constants/roles.js';
import { CLE_LETTRES_PRESTATIONS, validerTableDesLettres } from '../rules/numero-passage.js';
export const CATEGORIES_PARAMETRE = [
    'SECURITE',
    'DNS',
    'PARCOURS_PATIENT',
    'AYANTS_DROIT',
    'GENERAL',
];
export const TYPES_PARAMETRE = ['TEXTE', 'ENTIER', 'BOOLEEN'];
export const LIBELLE_CATEGORIE = {
    SECURITE: 'Sécurité et sessions',
    DNS: 'Déclarations DNS',
    PARCOURS_PATIENT: 'Parcours patient',
    AYANTS_DROIT: 'Ayants droit — pièces acceptées à l’accueil',
    GENERAL: 'Général',
};
/**
 * Un paramètre tel que l'écran le reçoit.
 *
 * La valeur reste une chaîne : c'est `type` qui dit comment la lire. Un
 * champ polymorphe côté API obligerait le front à un `unknown` et à un
 * cast — exactement ce que le schéma partagé sert à éviter.
 */
export const schemaParametre = z.object({
    cle: z.string(),
    categorie: z.enum(CATEGORIES_PARAMETRE),
    type: z.enum(TYPES_PARAMETRE),
    valeur: z.string(),
    valeurParDefaut: z.string(),
    libelle: z.string(),
    description: z.string(),
    unite: z.string().nullable(),
    bornMin: z.number().int().nullable(),
    bornMax: z.number().int().nullable(),
    modifiable: z.boolean(),
    modifieLe: z.iso.datetime().nullable(),
});
/** Modification d'un paramètre. La valeur arrive toujours en texte. */
export const schemaModificationParametre = z.object({
    // 2000 : la table des lettres s'allonge seule (décision 291).
    valeur: z.string().trim().min(1, 'Une valeur est requise.').max(2000),
    /**
     * La valeur que l'écran affichait (décision 291) : la table des lettres
     * s'allonge seule ; si elle a changé depuis, l'enregistrement est refusé (409).
     */
    valeurLue: z.string().max(2000).optional(),
});
// ─────────────────────────────────────────────────────────────────────
//  Règles d'alerte
// ─────────────────────────────────────────────────────────────────────
export const schemaRegleAlerte = z.object({
    code: z.string(),
    libelle: z.string(),
    description: z.string(),
    actif: z.boolean(),
    destinataires: z.array(z.enum(ROLES)),
    seuilJours: z.number().int().nullable(),
    modifieLe: z.iso.datetime().nullable(),
});
/**
 * Modification d'une règle. Tout est optionnel : on n'envoie que ce qui
 * change.
 *
 * `destinataires` peut valoir un tableau vide — « personne n'est prévenu »
 * est un état volontaire, pas une erreur de saisie. L'écran le signale, il
 * ne l'interdit pas : une alerte qu'on ne veut pas recevoir se désactive
 * plutôt que de rester active dans le vide.
 */
export const schemaModificationRegleAlerte = z
    .object({
    actif: z.boolean(),
    destinataires: z.array(z.enum(ROLES)).max(ROLES.length),
    seuilJours: z.number().int().min(1).max(3650).nullable(),
})
    .partial()
    .refine((valeur) => Object.keys(valeur).length > 0, {
    message: 'Aucune modification demandée.',
});
/**
 * Convertit la valeur textuelle d'un paramètre selon son type.
 *
 * Vit dans `shared` parce que l'API en a besoin pour lire un seuil, et le
 * front pour l'afficher dans le bon contrôle. Deux implémentations
 * divergeraient sur les cas limites — « 0 », « faux », une chaîne vide.
 */
export function lireParametre(parametre) {
    switch (parametre.type) {
        case 'ENTIER':
            return Number.parseInt(parametre.valeur, 10);
        case 'BOOLEEN':
            return parametre.valeur === 'true';
        case 'TEXTE':
            return parametre.valeur;
    }
}
/**
 * Qui modifie quel paramètre : `configuration:write` les modifie tous ; la
 * Direction (rôle de base, ADR 0006) modifie la seule table des lettres des
 * numéros de passage (décision 290). L'écran et l'API lisent la même règle.
 */
export function peutModifierParametre(compte, cle) {
    if (compte.permissions.includes('configuration:write'))
        return true;
    return cle === CLE_LETTRES_PRESTATIONS && compte.role === 'DIRECTION';
}
/**
 * Valide une valeur saisie contre le type et les bornes du paramètre.
 *
 * Rend `null` si tout va bien, sinon le message à afficher — en français, à
 * l'endroit où l'utilisateur agit.
 */
export function validerValeurParametre(parametre, valeur) {
    // Numéros de passage (R4) : la table des lettres a ses propres règles.
    if (parametre.cle === CLE_LETTRES_PRESTATIONS)
        return validerTableDesLettres(valeur);
    if (parametre.type === 'BOOLEEN') {
        return valeur === 'true' || valeur === 'false' ? null : 'Valeur attendue : vrai ou faux.';
    }
    if (parametre.type === 'TEXTE') {
        return valeur.trim() === '' ? 'Une valeur est requise.' : null;
    }
    if (!/^-?\d+$/.test(valeur.trim()))
        return 'Un nombre entier est attendu.';
    const nombre = Number.parseInt(valeur, 10);
    const unite = parametre.unite === null ? '' : ` ${parametre.unite}`;
    if (parametre.bornMin !== null && nombre < parametre.bornMin) {
        return `Valeur minimale : ${parametre.bornMin}${unite}.`;
    }
    if (parametre.bornMax !== null && nombre > parametre.bornMax) {
        return `Valeur maximale : ${parametre.bornMax}${unite}.`;
    }
    return null;
}
// ─────────────────────────────────────────────────────────────────────
//  Courriels envoyés — ce qui reste de l'offre du fournisseur SMTP
// ─────────────────────────────────────────────────────────────────────
/** Une journée (UTC, comme le compteur du fournisseur). */
export const schemaJourCourriels = z.object({
    jour: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    envoyes: z.number().int().nonnegative(),
    echecs: z.number().int().nonnegative(),
});
export const schemaSuiviCourriels = z.object({
    quotaJournalier: z.number().int().positive(),
    envoyesAujourdhui: z.number().int().nonnegative(),
    echecsAujourdhui: z.number().int().nonnegative(),
    /** Le prochain minuit UTC, où le fournisseur remet son compteur à zéro. */
    remiseAZeroLe: z.iso.datetime(),
    /** Les 30 derniers jours, du plus ancien à aujourd'hui, jours vides compris. */
    parJour: z.array(schemaJourCourriels),
});
// ─────────────────────────────────────────────────────────────────────
//  Fonctions activables — ce qui est codé mais ne part pas en v1
// ─────────────────────────────────────────────────────────────────────
/**
 * Décision du 13/10 : la lecture automatique (OCR) est hors v1. Son code
 * reste ; un interrupteur par fonction la cache, éteint par défaut. L'écran
 * lit cet état pour taire menus, boutons et textes qui y renvoient.
 */
export const schemaFonctions = z.object({
    /** OCR d'un DNS photographié ou scanné, PDF sans couche texte compris (CP-11). */
    ocrDns: z.boolean(),
    /** Lecture du texte d'un DNS en PDF natif, sans OCR (CP-11) — interrupteur à part. */
    pdfNatifDns: z.boolean(),
    /** Scan d'une feuille de coupure qui pré-remplit le registre (CP-12). */
    scanCoupure: z.boolean(),
    /** Les formulaires papier du mode dégradé, à imprimer avant une coupure (CP-12). */
    formulairesCoupure: z.boolean(),
    /** La caisse des patients non couverts (spécification du 16/10, R17). */
    caisse: z.boolean(),
    /** Pilotage → Activité des médecins (décision 165). */
    activiteMedecins: z.boolean(),
    /** La liste d'attente de la caisse et l'entrée en file à l'encaissement (décision 173). */
    fileCaisse: z.boolean(),
    /** Ma file : depuis quand le médecin reçoit son patient, depuis quand les autres attendent (variante A). */
    dureeEcoulee: z.boolean(),
    /** Pilotage → Tableau de bord, page d'arrivée de la Direction (décisions 214 et 220). */
    tableauDeBord: z.boolean(),
    /** L'import DNS « embauches et départs seulement » (décision 227). */
    dnsMouvements: z.boolean(),
    /** Écran d'appel : les prochains par spécialité et la voix « Manaraka tompoko » (décisions 253 à 255). */
    ecranProchains: z.boolean(),
    /** Numéros de passage : attribués à l'entrée, montrés à l'accueil (décisions 278, 280, 282). */
    numerosPassage: z.boolean(),
    /** Télé : la vidéo en boucle aussi en mode noms (décision 323). */
    videosTele: z.boolean(),
    /** AJ-02 (28/10) : le dentiste fixe le prix d'un non-couvert, payé à la caisse après la consultation. */
    prixDentiste: z.boolean(),
    /** Décisions 360 et 404 : un ayant droit entre sans confirmation si son salarié est au DNS en cours. Allumée par défaut. */
    ayantDroitSansConfirmation: z.boolean(),
    /**
     * Décisions 432 à 436 bis : LE Super administrateur fait tous les gestes,
     * dans le centre qu'il choisit. Allumée par défaut ; « non » rend le chemin
     * de la v1.0.1.
     */
    superAdminPartout: z.boolean(),
});
//# sourceMappingURL=configuration.js.map